import { ChevronDownIcon, Tag01Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { GroupedDatasetVersionSchemaWithOptionalAsOf } from '@/Pages/Dataset/types';
import { PopoverDropdownMenu } from '@/components/PopoverDropdownMenu';
import { Button, Tooltip } from '@/design-system';
import { DATASET_VERSION_FORMATTER } from '@/utils/version-format';

import { TITLE_ALL_VERSIONS } from '../constants';
import { getVersionName } from '../utils';
import { AddDatasetVersionTagModal } from './AddDatasetVersionTagModal';

export function DatasetVersionDropdownMenu(props: {
  selectedVersion: GroupedDatasetVersionSchemaWithOptionalAsOf;
  datasetId: string;
  versions: GroupedDatasetVersionSchemaWithOptionalAsOf[];
  size: number;
  setSize: (size: number) => void;
  totalVersions: number;
  setSearch: (search: string) => void;
  search: string;
  isLoadingCurrentVersion: boolean;
  isLoadingVersions: boolean;

  setSelectedVersion: (
    version: GroupedDatasetVersionSchemaWithOptionalAsOf
  ) => void;
  versionPageSize: number;
  className?: string;
  placeholder?: string;
  mutateVersions: () => void;
}) {
  const [addTagVersionId, setAddTagVersionId] =
    useState<GroupedDatasetVersionSchemaWithOptionalAsOf | null>(null);
  const hasMore = props.totalVersions > props.versions.length;

  return (
    <>
      <PopoverDropdownMenu
        values={props.versions}
        searchPlaceholder="Search By Tag..."
        controlledSearchTerm={props.search}
        enableSearch={true}
        setSearchTerm={props.setSearch}
        getTitle={(v) => getVersionName(v)}
        getKey={(v) => v.as_of ?? 'First version'}
        getSubtitle={(v) => {
          const tags = v.tags ?? [];
          return tags.length && v.as_of
            ? DATASET_VERSION_FORMATTER.format(new Date(v.as_of))
            : null;
        }}
        getIsTitleBold={(v) => !!v.tags && v.tags.length > 0}
        includeTitleInSearch={false}
        includeSubtitleInSearch={true}
        getIsSelected={(v) => props.selectedVersion?.as_of === v.as_of}
        onSelect={props.setSelectedVersion}
        hasMore={hasMore}
        currentSize={Math.floor(props.versions.length / props.versionPageSize)}
        onInfiniteScroll={() => {
          props.setSize(props.size + 1);
        }}
        contentWidth="350px"
        isLoading={props.isLoadingVersions}
        getEndDecorator={(
          version: GroupedDatasetVersionSchemaWithOptionalAsOf
        ) => {
          if (
            version.tags?.length === 1 &&
            version.tags[0] === TITLE_ALL_VERSIONS
          ) {
            return null;
          }
          return (
            <div className="opacity-0 transition-opacity group-hover:opacity-100">
              <Tooltip title="Tag this version">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setAddTagVersionId(version);
                  }}
                  className="flex items-center justify-center rounded-md border border-secondary bg-background p-1 hover:bg-tertiary"
                >
                  <Tag01Icon className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          );
        }}
        showEndDecoratorBeforeCheck={true}
      >
        <Button
          size="md"
          variant="outlined"
          color="secondary"
          rightDecorator={ChevronDownIcon}
        >
          {props.selectedVersion
            ? getVersionName(props.selectedVersion)
            : props.placeholder ?? 'Latest'}
        </Button>
      </PopoverDropdownMenu>
      {addTagVersionId && (
        <AddDatasetVersionTagModal
          open={true}
          onClose={() => setAddTagVersionId(null)}
          datasetId={props.datasetId}
          selected={addTagVersionId}
          mutate={props.mutateVersions}
        />
      )}
    </>
  );
}
